import axios from 'axios';
import * as rax from 'retry-axios';
import { hostedRegionTimings } from 'utils/constants';

const defaultCustomerRegion = 'APAC';
let abortPoll = false;
let customerRegion = defaultCustomerRegion;
let startTimestamp = null;
let secondMessageDisplayed = false;
let thirdMessageDisplayed = false;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const genericError = 'GENERIC_ERROR';
export const timeoutError = 'POLLING_TIMEOUT';
export const abortError = 'POLLING_ABORT';
export const pollNewInstanceError = 'NO_SUCCESSFUL_POLL';
export const journeyCompleted = 'JOURNEY_COMPLETED';

const updateMessaging = (secondsElapsed, dispatch) => {
  const [fiftiethPercentile, nintiethPercentile] = hostedRegionTimings[
    customerRegion
  ];

  if (
    !secondMessageDisplayed &&
    secondsElapsed >= fiftiethPercentile &&
    secondsElapsed < nintiethPercentile
  ) {
    dispatch({ type: 'SHOW_SECOND_WAITING_MESSAGE' });
    secondMessageDisplayed = true;
  } else if (!thirdMessageDisplayed && secondsElapsed >= nintiethPercentile) {
    dispatch({ type: 'SHOW_THIRD_WAITING_MESSAGE' });
    thirdMessageDisplayed = true;
  }
};

export function secondsElapsed() {
  return Math.floor(Date.now() / 1000) - Math.floor(startTimestamp / 1000);
}

const defaultPollNewInstanceConfig = {
  backoffType: 'static',
  noResponseRetries: 90,
  retry: 90,
  retryDelay: 2000,
  successStatus: 200,
  successfulPings: 5,
  successfulPingDelay: 500,
  // A placeholder. Will be overwritten in `pollNewInstance`
  // to allow us to determine the max length of time before
  // we time out.
  maxTime: null,
  setMaxTime: function (maxTime) {
    this.maxTime = maxTime;
  },
  // A placeholder. Will be overwritten in `pollNewInstance`
  // to allow us to use `dispatch` inside our
  // `onRetryAttempt` function.
  dispatch: null,
  setDispatch: function (dispatch) {
    this.dispatch = dispatch;
  },
  onRetryAttempt: (err) => {
    const { url: pingUrl } = err.config;
    const { currentRetryAttempt, dispatch } = rax.getConfig(err);
    updateMessaging(secondsElapsed(), dispatch);
    console.log(
      `No response from '${pingUrl}' on retry attempt #${currentRetryAttempt}.`,
    );
  },
  shouldRetry: (err) => {
    const { maxTime } = rax.getConfig(err);

    if (secondsElapsed() >= maxTime || abortPoll) {
      return false;
    }
    return true;
  },
};

// Attach retry interceptor to axios
rax.attach();

export const pollNewInstance = async (
  redirectURL,
  dispatch,
  config = defaultPollNewInstanceConfig,
) => {
  // Pass `dispatch` into the config object
  // to be used in the `onRetryAttempt` function
  if (!config.dispatch) {
    config.setDispatch(dispatch);
  }

  // Pass `maxTime` from customer-selected region
  // into config object to be used in the `shouldRetry`
  // function
  if (!config.maxTime) {
    config.setMaxTime(hostedRegionTimings[customerRegion].slice(2));
  }

  const { successStatus, successfulPings, successfulPingDelay } = config;

  const newInstancePingLocation = `${new URL(redirectURL).origin}/ping`;

  try {
    const res = await axios.get(newInstancePingLocation, {
      raxConfig: config,
    });

    if (res.status === successStatus) {
      const requiredSuccessfulPings = successfulPings - 1;

      if (requiredSuccessfulPings > 0) {
        console.log(
          `Successfully pinged ${newInstancePingLocation}, pinging ${requiredSuccessfulPings} more ${
            requiredSuccessfulPings > 1 ? 'times' : 'time'
          } before redirect.`,
        );

        await sleep(successfulPingDelay);
        return pollNewInstance(redirectURL, dispatch, {
          ...config,
          successfulPings: requiredSuccessfulPings,
        });
      }
      // Dispatch an action, complete ProgressBar animation,
      // then return the redirect URL
      dispatch({ type: journeyCompleted });
      await sleep(1000);
      return Promise.resolve(redirectURL);
    }
  } catch (error) {
    return Promise.reject(pollNewInstanceError);
  }
};

export const defaultConfigs = {
  attempts: 90, // poll up to 3 mins
  interval: 2000,
  successStatus: 'PROVISIONED',
  errorStatus: 'ERROR',
};

const poll = async (uri, dispatch, config = defaultConfigs) => {
  const { interval, successStatus, errorStatus, attempts } = config;
  const remainingAttempts = attempts - 1;
  const maxTime = hostedRegionTimings[customerRegion].slice(2);

  updateMessaging(secondsElapsed(), dispatch);

  await sleep(interval);
  const res = await axios.get(uri);

  if (abortPoll) {
    return Promise.reject(abortError);
  }

  if (res.data.status === errorStatus) {
    return Promise.reject(genericError);
  }

  if (res.data.status === successStatus) {
    return Promise.resolve(res.data.redirectURL);
  }

  if (secondsElapsed() >= maxTime) {
    return Promise.reject(timeoutError);
  }

  return poll(uri, dispatch, { ...config, attempts: remainingAttempts });
};

export function startPoll(uri, region, dispatch, config) {
  customerRegion = region || defaultCustomerRegion;

  // Set `startTimestamp` for us to measure time against
  startTimestamp = Date.now();

  abortPoll = false;
  return poll(uri, dispatch, config);
}

export function stopPoll() {
  abortPoll = true;
}

export const resolveInstanceURL = (hostname) => {
  if (window.location.hostname.includes('local')) {
    return `https://${hostname}.dev.xmatters.com`;
  }
  return `https://${hostname}${window.location.hostname.replace(
    'provision',
    '',
  )}`;
};

export const ruleListTextStrings = {
  dictionaryMatching:
    'Not contain dictionary words, a company or application name, or a commonly used password',
  noLeadingOrTrailingSpaces: 'Not start or end with a space',
  passwordMinimumMaximumLength: (min, max) => `Be ${min} to ${max} characters`,
  requiresLowercaseLetter: 'Contain 1 or more lowercase letters (a – z)',
  requiresUppercaseLetter: 'Contain 1 or more uppercase letters (A - Z)',
  requiresSpecialCharacter: 'Contain 1 or more special characters (!@#$% etc)',
};
